import React from 'react';
import { Box, Tooltip, CircularProgress, Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import ShutterCapture from './ShutterCapture';
import { ExportIcon, CaptureIconNew } from 'components/CommonIcons/Icons';
import { ReactComponent as ScreenshotBlueIcon } from '../assets/PepWise2.0/Screenshot-blue.svg'

const CORNER_STYLES = [
  { key: 'top-left', top: 0, left: 0, borderTop: '2px solid black', borderLeft: '2px solid black' },
  { key: 'top-right', top: 0, right: 0, borderTop: '2px solid black', borderRight: '2px solid black' },
  { key: 'bottom-left', bottom: 0, left: 0, borderBottom: '2px solid black', borderLeft: '2px solid black' },
  { key: 'bottom-right', bottom: 0, right: 0, borderBottom: '2px solid black', borderRight: '2px solid black' },
];

export interface CaptureExportControlsProps {
  onScreenshot: () => void;
  screenCapturing: boolean;
  onExport?: () => void;
  isExporting?: boolean;
  isVisible?: boolean;
  exportIcon?: React.ReactNode;
  exportTooltip?: React.ReactNode;
  exportTooltipPlacement?: 'left' | 'top' | 'right' | 'bottom' | 'left-start' | 'left-end' | 'right-start' | 'right-end';
  showExport?: boolean;
  type?: 'pipeline' | 'snow' | 'incident' | 'platform' | 'azure';
  sx?: SxProps<Theme>;
  exportLabel?: string;
  exportsx?: SxProps<Theme>;
}

const CaptureExportControls: React.FC<CaptureExportControlsProps> = ({ 
  onScreenshot, 
  screenCapturing, 
  onExport, 
  isExporting, 
  isVisible = true,
  exportIcon,
  exportTooltip,
  exportTooltipPlacement = 'left',
  showExport = true,
  type = 'pipeline',
  sx,
  exportLabel = 'Export to Excel',
  exportsx
}) => {
  const theme = useTheme();
  const p = theme.palette as any;

  const defaultCameraSx: SxProps<Theme> = {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: p.white?.main || '#fff',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: screenCapturing ? 'default' : 'pointer',
      overflow: 'hidden',
      transition: 'opacity 0.2s ease',
      '&:hover': { opacity: screenCapturing ? 1 : 0.75 },
  };

  let containerSx: SxProps<Theme> = { display: isVisible ? 'flex' : 'none', alignItems: 'center', justifyContent: 'flex-end', flexDirection: 'column', gap: 1 };

  // Preserve layout wrapper styling for pipeline so it aligns correctly
  if (type === 'pipeline') {
    containerSx = { ...containerSx, ml: 'auto', alignSelf: 'flex-end', zIndex: 5 };
  }

  // Apply custom sx overrides
  if (sx) {
    containerSx = { ...containerSx, ...sx };
  }

  // Enforce consistent identical visuals across all types
  const cameraConfig: SxProps<Theme> = { width: 44, height: 44, p: 0.5 };
  const exportConfig: SxProps<Theme> = { width: 44, height: 44, px: 0.5, py: 0.5 };
  const exportFontSize = '0.55rem';
  const progressSize = 30;
  const showExportingText = false;
  const exportTextColor = p.grey?.[900] || '#000';
  const exportIconRender = exportIcon || <ExportIcon />;

  return (
    <Box sx={containerSx}>
      <Box
        id="exclude-camera"
        onClick={onScreenshot}
        sx={{ ...defaultCameraSx, ...cameraConfig }}
      >
        <Tooltip title="Take Screenshot" arrow placement='left-start'>
          <Box sx={{ display: 'flex', flexDirection:'column', alignItems: 'center', justifyContent: 'center' }}>
            {type === 'platform' ? <ScreenshotBlueIcon /> : <CaptureIconNew />}
            <Typography sx={{ fontSize: exportFontSize, color: exportTextColor, fontWeight: 500, lineHeight: 1, textAlign: 'center', mt: 0.2 }}>
              Take <br /> Screenshot
            </Typography>
          </Box>
        </Tooltip>
        <ShutterCapture
          active={screenCapturing}
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
          }}
        />
      </Box>

      {showExport && (
          <Box
            id="exclude-export"
            title={exportTooltip ? undefined : exportLabel}
            onClick={onExport}
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: isExporting ? 'default' : 'pointer',
              backgroundColor: p.white?.main || '#fff',
              overflow: 'hidden',
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: isExporting ? 1 : 0.75 },
              ...exportConfig,
              ...exportsx
            }}
          >
            {isExporting ? (
              <>
                <CircularProgress size={progressSize} thickness={3} sx={{ color: p.primary?.main }} />
                {showExportingText && (
                  <Typography sx={{ fontSize: '10px', color: '#000000', fontWeight: 500, lineHeight: '25px' }}>
                    Exporting...
                  </Typography>
                )}
              </>
            ) : exportTooltip ? (
              <Tooltip title={exportTooltip} arrow placement={exportTooltipPlacement}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                  {exportIconRender}
                  <Typography sx={{ fontSize: exportFontSize, color: exportTextColor, fontWeight: 500, lineHeight: 1 }}>
                    Export
                  </Typography>
                </Box>
              </Tooltip>
            ) : (
              <>
                {exportIconRender}
                <Typography sx={{ fontSize: exportFontSize, color: exportTextColor, fontWeight: 500, lineHeight: 1 }}>
                  Export
                </Typography>
              </>
            )}
          </Box>
      )}
    </Box>
  );
};

export default CaptureExportControls;
